import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './Settings.css';
import { 
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import apiClient from '../lib/api';
// Billing components removed - Brightstone internal use only
// SettingsTeamMembers removed - Brightstone internal use only

interface CompanyInfo {
  company_id: string;
  subscription_tier: string;
  max_properties: number;
  max_users: number;
  contact_email?: string;
  created_at?: string;
}

interface UserInfo {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  created_at?: string;
  last_login?: string | null;
}

interface CurrentUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company_id: number;
  role: string;
}

// Billing interfaces removed - Brightstone internal use only


const Settings: React.FC = () => {
  const location = useLocation();
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  
  const [contactEmail, setContactEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [editing, setEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  // Invite form state removed - Brightstone internal use only
  const [activeTab, setActiveTab] = useState<'company'>('company');
  
  // Security data state
  const [sessions, setSessions] = useState<any[]>([]);
  const [passwordHistory, setPasswordHistory] = useState<any>(null);
  const [twoFactorStatus, setTwoFactorStatus] = useState<any>(null);
  
  // 2FA Setup state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFASetupData, setTwoFASetupData] = useState<any>(null);
  const [twoFAVerificationCode, setTwoFAVerificationCode] = useState("");
  const [twoFASetupStep, setTwoFASetupStep] = useState<'setup' | 'verify'>('setup');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ open: boolean; tone: 'success' | 'error' | 'info' | 'warning'; message: string }>({ 
    open: false, 
    tone: 'info', 
    message: '' 
  });
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    isDestructive: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    isDestructive: false,
    onConfirm: () => {}
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Always fetch current user info
      const userData = await apiClient.auth.me();
      setCurrentUser(userData);
      setFirstName(userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : "");
      setLastName(userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : "");
      setUserEmail(userData.email || "");
      
      if (activeTab === 'company') {
        try {
          const [companyData, usersData] = await Promise.all([
            apiClient.companies.getInfo().catch((err) => {
              console.error('Error fetching company info:', err);
              return null;
            }),
            apiClient.companies.getUsers().catch((err) => {
              console.error('Error fetching users:', err);
              // Return empty array if user doesn't have permission
              if (err.response?.status === 403) {
                console.log('User does not have admin privileges');
                setNotice({ 
                  open: true, 
                  tone: 'warning', 
                  message: 'You do not have permission to view users. Admin access required.' 
                });
              }
              return [];
            })
          ]);
          
          if (companyData) {
            setCompany(companyData);
            setContactEmail(companyData.contact_email || "");
          }
          setUsers(usersData || []);
        } catch (error) {
          console.error('Error in company/users tab:', error);
          setUsers([]);
        }
      }
      
      // Billing tab removed - Brightstone internal use only
      
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      // Set appropriate error message based on the tab
      if (activeTab === 'users' && err.response?.status === 403) {
        // Don't show error for users tab if permission denied, just show empty state
        setUsers([]);
      } else if (activeTab === 'account') {
        // For account tab, we already have the user data, so no error needed
        console.log('Account tab loaded with existing user data');
      } else {
        setError('Error loading settings data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchSecurityData = useCallback(async () => {
    try {
      const [sessionsData, passwordHistoryData, twoFactorData] = await Promise.all([
        apiClient.security.getSessions().catch((err) => {
          console.error('Error fetching sessions:', err);
          return [];
        }),
        apiClient.security.getPasswordHistory().catch((err) => {
          console.error('Error fetching password history:', err);
          return [];
        }),
        apiClient.twoFactor.getStatus().catch((err) => {
          console.error('Error fetching 2FA status:', err);
          return { enabled: false, has_backup_codes: false };
        })
      ]);
      
      setSessions(sessionsData);
      setPasswordHistory(passwordHistoryData);
      setTwoFactorStatus(twoFactorData);
      
      console.log('🔍 Security data fetched:', {
        sessions: sessionsData,
        passwordHistory: passwordHistoryData,
        twoFactorStatus: twoFactorData
      });
    } catch (err) {
      console.error('Error fetching security data:', err);
      // Set default values to prevent component from breaking
      setSessions([]);
      setPasswordHistory([]);
      setTwoFactorStatus({ enabled: false, has_backup_codes: false });
    }
  }, []);

  const handle2FAEnable = async () => {
    try {
      console.log('🔐 Starting 2FA setup...');
      const setupData = await apiClient.twoFactor.setup();
      console.log('✅ 2FA setup data received:', setupData);
      
      // Set modal state immediately
      setTwoFASetupData(setupData);
      setShow2FASetup(true);
      setTwoFASetupStep('setup');
      
      console.log('🎯 Modal should be showing now');
    } catch (err: any) {
      console.error('❌ 2FA setup error:', err);
      setNotice({ open: true, tone: 'error', message: err.response?.data?.detail || 'Failed to setup 2FA.' });
    }
  };

  const handle2FAVerify = async () => {
    try {
      await apiClient.twoFactor.verify(twoFAVerificationCode);
      await apiClient.twoFactor.enable(twoFAVerificationCode);
      
      setNotice({ open: true, tone: 'success', message: '2FA enabled successfully!' });
      setShow2FASetup(false);
      setTwoFAVerificationCode("");
      await fetchSecurityData(); // Refresh 2FA status
    } catch (err: any) {
      setNotice({ open: true, tone: 'error', message: err.response?.data?.detail || 'Failed to verify 2FA code.' });
    }
  };

  const handle2FADisable = async () => {
    const password = prompt("Enter your password to disable 2FA:");
    if (!password) return;
    
    try {
      await apiClient.twoFactor.disable(password);
      setNotice({ open: true, tone: 'success', message: '2FA disabled successfully!' });
      await fetchSecurityData(); // Refresh 2FA status
    } catch (err: any) {
      setNotice({ open: true, tone: 'error', message: err.response?.data?.detail || 'Failed to disable 2FA.' });
    }
  };

  useEffect(() => {
    fetchData();
    if (activeTab === 'account') {
      fetchSecurityData();
    }
  }, [fetchData, fetchSecurityData, activeTab]);

  // Debug modal state
  useEffect(() => {
    console.log('🔍 Modal state changed:', { show2FASetup, twoFASetupStep, twoFASetupData: !!twoFASetupData });
  }, [show2FASetup, twoFASetupStep, twoFASetupData]);

  // Handle URL-based navigation for settings sections
  useEffect(() => {
    const pathname = location.pathname;
    if (pathname.includes('/settings/account') || pathname.includes('/settings/company')) {
      setActiveTab('company');
    } else {
      setActiveTab('company');
    }
  }, [location.pathname]);

  const updateCompany = async () => {
    try {
      // Update profile first (first_name, last_name)
      if (firstName || lastName) {
        await apiClient.companies.updateProfile({ first_name: firstName, last_name: lastName });
      }
      
      // Update company settings (contact_email)
      if (contactEmail) {
        await apiClient.companies.updateSettings({ contact_email: contactEmail });
      }
      
      setNotice({ open: true, tone: 'success', message: 'Settings updated successfully!' });
      setEditing(false);
    } catch (err: any) {
      console.error('❌ Error updating settings:', err);
      setNotice({ open: true, tone: 'error', message: err.response?.data?.detail || 'Failed to update settings.' });
    }
  };

  const inviteUser = async () => {
    // Validation
    if (!inviteFirstName || !inviteLastName || !inviteEmail) {
      setNotice({ open: true, tone: 'error', message: 'Please fill in all fields.' });
      return;
    }
    
    try {
      await apiClient.companies.inviteUser({ 
        email: inviteEmail, 
        first_name: inviteFirstName,
        last_name: inviteLastName,
        role: "analyst" 
      });
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      setNotice({ open: true, tone: 'success', message: 'User invitation sent successfully!' });
      fetchData(); // Refresh users list
    } catch (err) {
      setNotice({ open: true, tone: 'error', message: 'Failed to send invitation.' });
    }
  };

  // Billing functions removed - Brightstone internal use only

  const updateRole = async (id: string, role: string) => {
    await apiClient.companies.updateUserRole(id, role);
  };

  const removeUser = async (id: string) => {
    await apiClient.companies.removeUser(id);
  };

  // const renewSubscription = async () => {
  //   try {
  //     const data = await apiClient.stripe.renewSubscription();
  //     if (data.checkout_url) {
  //       window.location.href = data.checkout_url;
  //     } else {
  //       setNotice({ open: true, tone: 'error', message: 'Failed to get renewal checkout URL' });
  //     }
  //   } catch (err) {
  //     setNotice({ open: true, tone: 'error', message: 'Failed to renew subscription' });
  //   }
  // };

  // Note: Using per-property subscription model at $50/month per property

  return (
    <div className="settings-page">
      <div className="settings-container">
        <h1 className="page-title">Settings</h1>
        
        {/* Tab Navigation - Always show this */}
        <div className="tab-navigation">
          <button 
            onClick={() => {
              setError(null);  // Clear any error when switching tabs
              setActiveTab('company');
            }} 
            className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
          >
            Account
          </button>
          {/* Users tab removed - Brightstone internal use only */}
        </div>
        
        {/* Show loading within the tab content area */}
        {loading ? (
          <div className="loading-container" style={{ padding: '40px', textAlign: 'center' }}>
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: '10px', color: '#6b7280' }}>Loading...</p>
          </div>
        ) : (
          <>
            {/* Show error if present but don't block tab content */}
            {error && (
              <div className="error-message" style={{ 
                padding: '10px 20px', 
                marginBottom: '20px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {/* Account Tab */}
            {activeTab === 'company' && (
          <div className="tab-content">
            <div className="settings-grid">
              {/* Personal Information Card */}
              <div className="settings-card">
                <div className="card-header">
                  <h2 className="card-title">Personal Information</h2>
                  <button 
                    className="btn btn-small btn-secondary" 
                    onClick={() => setEditing(!editing)}
                  >
                    {editing ? 'Editing...' : 'Edit'}
                  </button>
                </div>
                
                <div className="card-description">
                  Update your personal details and contact information
                </div>

                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input 
                        type="text" 
                        className="form-input" 
                        value={firstName} 
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={!editing}
                      />
                      <div className="form-help">Your first name</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input 
                        type="text" 
                        className="form-input" 
                        value={lastName} 
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={!editing}
                      />
                      <div className="form-help">Your last name</div>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input 
                      type="email" 
                      className="form-input" 
                      value={userEmail} 
                      disabled
                    />
                    <div className="form-help">Your email address cannot be changed</div>
                  </div>
                </div>

                {editing && (
                  <div className="form-actions">
                    <button onClick={updateCompany} className="btn btn-primary">
                      Save Changes
                    </button>
                    <button 
                      onClick={() => { 
                        setEditing(false); 
                        setContactEmail(company?.contact_email || ""); 
                        setFirstName(currentUser?.first_name ? currentUser.first_name.charAt(0).toUpperCase() + currentUser.first_name.slice(1).toLowerCase() : ""); 
                        setLastName(currentUser?.last_name ? currentUser.last_name.charAt(0).toUpperCase() + currentUser.last_name.slice(1).toLowerCase() : ""); 
                      }} 
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Security Settings Card */}
              <div className="settings-card">
                <div className="card-header">
                  <h2 className="card-title">Security Settings</h2>
                </div>
                
                <div className="card-description">
                  Manage your account security and authentication preferences
                </div>

                {/* Password Section */}
                <div className="security-item">
                  <div className="security-info">
                    <h4>Password</h4>
                    <p>
                      {passwordHistory?.last_changed 
                        ? `Last changed ${new Date(passwordHistory.last_changed).toLocaleDateString('en-CA', { 
                            timeZone: 'America/Toronto',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          })}`
                        : 'Password never changed'
                      }
                    </p>
                  </div>
                  <button 
                    className="btn btn-small btn-secondary" 
                    onClick={() => setShowPasswordChange(!showPasswordChange)}
                  >
                    Change Password
                  </button>
                </div>

                {showPasswordChange && (
                  <div className="collapsible-section">
                    <div className="collapsible-content open">
                      <div className="form-section">
                        <div className="form-group">
                          <label className="form-label">Current Password</label>
                          <input 
                            type="password" 
                            className="form-input" 
                            placeholder="Enter your current password"
                          />
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">New Password</label>
                            <input 
                              type="password" 
                              className="form-input" 
                              placeholder="Enter new password"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Confirm New Password</label>
                            <input 
                              type="password" 
                              className="form-input" 
                              placeholder="Confirm new password"
                            />
                          </div>
                        </div>
                        <div className="form-actions">
                          <button className="btn btn-primary">Update Password</button>
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => setShowPasswordChange(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Two-Factor Authentication Section */}
                <div className="security-item">
                  <div className="security-info">
                    <h4>Two-Factor Authentication</h4>
                    <p>Add an extra layer of security to your account</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {twoFactorStatus?.enabled ? (
                      <>
                        <span className="status-badge status-enabled">✓ Enabled</span>
                        <button className="btn btn-small btn-danger" onClick={handle2FADisable}>Disable</button>
                      </>
                    ) : (
                      <>
                        <span className="status-badge status-disabled">✗ Disabled</span>
                        <button className="btn btn-small btn-primary" onClick={handle2FAEnable}>Enable</button>
                      </>
                    )}
                  </div>
                </div>


                {/* Login Sessions Section */}
                <div className="security-item">
                  <div className="security-info">
                    <h4>Active Sessions</h4>
                    <p>Manage your active login sessions</p>
                  </div>
                  <button 
                    className="btn btn-small btn-secondary" 
                    onClick={() => setShowSessions(!showSessions)}
                  >
                    View Sessions
                  </button>
                </div>

                {showSessions && (
                  <div className="collapsible-section">
                    <div className="collapsible-content open">
                      <div className="form-section">
                        {sessions.length > 0 ? (
                          sessions.map((session, index) => (
                            <div key={index} className="session-item">
                              <div className="session-info">
                                <div className="session-title">{session.device_info}</div>
                                <div className="session-details">{session.browser_info} • {session.location}</div>
                                <div className="session-time">
                                  Last active: {new Date(session.last_active).toLocaleString('en-CA', { 
                                    timeZone: 'America/Toronto',
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: true
                                  })}
                                </div>
                              </div>
                              {session.is_current ? (
                                <span className="status-badge status-enabled">Current</span>
                              ) : (
                                <button className="btn btn-small btn-danger">Revoke</button>
                              )}
                            </div>
                          ))
                        ) : (
                          <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                            No active sessions found
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Account Actions Card */}
              <div className="settings-card">
                <div className="card-header">
                  <h2 className="card-title">Account Actions</h2>
                </div>
                
                <div className="card-description">
                  Manage your account data and preferences
                </div>

                <div className="form-section">
                  <div className="security-item">
                    <div className="security-info">
                      <h4>Export Data</h4>
                      <p>Download a copy of your account data</p>
                    </div>
                    <button className="btn btn-small btn-secondary">Export</button>
                  </div>

                  <div className="security-item">
                    <div className="security-info">
                      <h4>Delete Account</h4>
                      <p>Permanently delete your account and all data</p>
                    </div>
                    <button className="btn btn-small btn-danger">Delete Account</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Users tab removed - Brightstone internal use only */}

        {/* Billing tab removed - Brightstone internal use only */}
        </>
        )}

        {/* Notice Modal */}
        {notice.open && (
          <div className="modal-overlay">
            <div className="notice-modal">
              <div className={`notice-content ${notice.tone}`}>
                {notice.tone === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{notice.message}</span>
              </div>
              <button 
                onClick={() => setNotice({ ...notice, open: false })} 
                className="modal-close"
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmationModal.isOpen && (
          <div className="modal-overlay">
            <div className="confirmation-modal">
              <h3 className="modal-title">{confirmationModal.title}</h3>
              <p className="modal-message">{confirmationModal.message}</p>
              <div className="modal-actions">
                <button 
                  onClick={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
                  className="modal-cancel"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmationModal.onConfirm}
                  className={`modal-confirm ${confirmationModal.isDestructive ? 'destructive' : ''}`}
                >
                  {confirmationModal.confirmText}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* 2FA Setup Modal */}
        {show2FASetup && (
          <div className="modal-overlay" onClick={() => setShow2FASetup(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Setup Two-Factor Authentication</h2>
                <button className="modal-close" onClick={() => setShow2FASetup(false)}>×</button>
              </div>
              
              <div className="modal-body">
                {twoFASetupStep === 'setup' ? (
                  <div className="twofa-setup">
                    <div className="setup-step">
                      <h3>Step 1: Install an Authenticator App</h3>
                      <p>Download one of these authenticator apps on your mobile device:</p>
                      <div className="auth-apps">
                        <div className="auth-app">
                          <strong>Google Authenticator</strong>
                          <p>Available for iOS and Android</p>
                        </div>
                        <div className="auth-app">
                          <strong>Microsoft Authenticator</strong>
                          <p>Available for iOS and Android</p>
                        </div>
                        <div className="auth-app">
                          <strong>Authy</strong>
                          <p>Available for iOS, Android, and Desktop</p>
                        </div>
                      </div>
                    </div>

                    <div className="setup-step">
                      <h3>Step 2: Scan QR Code</h3>
                      <p>Open your authenticator app and scan this QR code:</p>
                      <div className="qr-code-container">
                        {twoFASetupData?.qr_code ? (
                          <img src={twoFASetupData.qr_code} alt="2FA QR Code" className="qr-code" />
                        ) : (
                          <div className="qr-code-placeholder">Loading QR Code...</div>
                        )}
                      </div>
                      <p className="manual-code-label">Or manually enter this code:</p>
                      <div className="manual-code">
                        <code>{twoFASetupData?.secret}</code>
                        <button 
                          className="copy-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(twoFASetupData?.secret || '');
                            setNotice({ open: true, tone: 'success', message: 'Secret code copied to clipboard!' });
                          }}
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="setup-step">
                      <h3>Step 3: Save Backup Codes</h3>
                      <p>Save these backup codes in a secure location. You can use them to access your account if you lose your phone:</p>
                      <div className="backup-codes">
                        {twoFASetupData?.backup_codes?.map((code: string, index: number) => (
                          <div key={index} className="backup-code">
                            <code>{code}</code>
                            <button 
                              className="copy-btn"
                              onClick={() => {
                                navigator.clipboard.writeText(code);
                                setNotice({ open: true, tone: 'success', message: 'Backup code copied!' });
                              }}
                            >
                              Copy
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="backup-warning">⚠️ Each backup code can only be used once. Store them securely!</p>
                    </div>

                    <div className="modal-actions">
                      <button className="btn btn-primary" onClick={() => setTwoFASetupStep('verify')}>
                        Next: Verify Setup
                      </button>
                      <button className="btn btn-secondary" onClick={() => setShow2FASetup(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="twofa-verify">
                    <div className="verify-step">
                      <h3>Step 4: Verify Setup</h3>
                      <p>Enter the 6-digit code from your authenticator app to complete the setup:</p>
                      
                      <div className="verification-input">
                        <input
                          type="text"
                          placeholder="000000"
                          value={twoFAVerificationCode}
                          onChange={(e) => setTwoFAVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="form-input verification-code-input"
                          maxLength={6}
                        />
                      </div>
                      
                      <p className="verification-help">
                        Open your authenticator app and enter the 6-digit code that appears.
                      </p>
                    </div>

                    <div className="modal-actions">
                      <button 
                        className="btn btn-primary" 
                        onClick={handle2FAVerify}
                        disabled={twoFAVerificationCode.length !== 6}
                      >
                        Enable 2FA
                      </button>
                      <button className="btn btn-secondary" onClick={() => setTwoFASetupStep('setup')}>
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Two-Factor Authentication Section Component
// TwoFactorAuthSection removed - functionality integrated into main settings page
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TwoFactorAuthSection_OLD: React.FC = () => {
  const [status, setStatus] = useState<{ enabled: boolean; has_backup_codes: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState<{ secret: string; qr_code: string; backup_codes: string[] } | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'status' | 'setup' | 'verify' | 'disable'>('status');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await apiClient.twoFactor.getStatus();
      setStatus(data);
    } catch (err) {
      setError('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    try {
      setError(null);
      setMessage(null);
      const data = await apiClient.twoFactor.setup();
      setSetupData(data);
      setStep('setup');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to setup 2FA');
    }
  };

  const handleVerifyAndEnable = async () => {
    try {
      setError(null);
      setMessage(null);
      
      // First verify the code
      await apiClient.twoFactor.verify(verificationCode);
      
      // Then enable 2FA
      await apiClient.twoFactor.enable(verificationCode);
      
      setMessage('Two-factor authentication enabled successfully!');
      setVerificationCode('');
      setSetupData(null);
      setStep('status');
      await loadStatus();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid verification code');
    }
  };

  const handleDisable = async () => {
    try {
      setError(null);
      setMessage(null);
      
      await apiClient.twoFactor.disable(disablePassword, disableCode || undefined);
      
      setMessage('Two-factor authentication disabled successfully');
      setDisablePassword('');
      setDisableCode('');
      setStep('status');
      await loadStatus();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to disable 2FA');
    }
  };

  const handleRegenerateBackupCodes = async () => {
    try {
      setError(null);
      setMessage(null);
      
      const data = await apiClient.twoFactor.regenerateBackupCodes();
      setSetupData({ ...setupData!, backup_codes: data.backup_codes });
      setStep('setup');
      setMessage('Backup codes regenerated. Please save them securely.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to regenerate backup codes');
    }
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backup_codes) return;
    
    const content = `CityScrape 2FA Backup Codes\n\nThese are single-use recovery codes. Keep them safe!\n\n${setupData.backup_codes.join('\n')}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cityscrape-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="settings-card">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2 className="section-title">Security Settings</h2>
        <p className="section-description">Manage your account security and authentication</p>
      </div>
      <div className="settings-card">
        <div className="card-header">
          <h3 className="card-title">Two-Factor Authentication</h3>
          {status?.enabled && (
            <span style={{ color: '#BADA55', fontWeight: 600, fontSize: 14 }}>
              ✓ Enabled
            </span>
          )}
        </div>

      {message && <div className="form-message success">{message}</div>}
      {error && <div className="form-message error">{error}</div>}

      {/* Status View */}
      {step === 'status' && (
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 20, color: '#6b7280' }}>
            Add an extra layer of security to your account. When enabled, you'll need to enter a code from your authenticator app in addition to your password when logging in.
          </p>

          {!status?.enabled ? (
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Get Started</h3>
              <p style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
                You'll need an authenticator app like Microsoft Authenticator, Google Authenticator, or Authy.
              </p>
              <button 
                onClick={handleSetup}
                className="save-btn"
              >
                Enable Two-Factor Authentication
              </button>
            </div>
          ) : (
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>2FA is Active</h3>
              <p style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
                Your account is protected with two-factor authentication.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button 
                  onClick={() => setStep('disable')}
                  className="cancel-btn"
                >
                  Disable 2FA
                </button>
                <button 
                  onClick={handleRegenerateBackupCodes}
                  className="edit-btn"
                >
                  Regenerate Backup Codes
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Setup View */}
      {step === 'setup' && setupData && (
        <div style={{ padding: '20px 0' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Scan QR Code</h3>
          
          <div style={{ marginBottom: 24 }}>
            <p style={{ marginBottom: 12, color: '#6b7280', fontSize: 14 }}>
              1. Open your authenticator app (Microsoft Authenticator, Google Authenticator, etc.)
            </p>
            <p style={{ marginBottom: 12, color: '#6b7280', fontSize: 14 }}>
              2. Scan this QR code:
            </p>
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, display: 'inline-block', border: '1px solid #e5e7eb' }}>
              <img src={setupData.qr_code} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
            </div>
            <p style={{ marginTop: 12, color: '#6b7280', fontSize: 12 }}>
              Can't scan? Enter this code manually: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace' }}>{setupData.secret}</code>
            </p>
          </div>

          <div style={{ marginBottom: 24, padding: 16, background: '#fef3c7', borderRadius: 8, border: '1px solid #fbbf24' }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>⚠️ Save Your Backup Codes</h4>
            <p style={{ fontSize: 13, color: '#92400e', marginBottom: 12 }}>
              These codes can be used to access your account if you lose your authenticator device. Each code can only be used once.
            </p>
            <div style={{ background: '#fff', padding: 12, borderRadius: 6, fontFamily: 'monospace', fontSize: 13, marginBottom: 12 }}>
              {setupData.backup_codes.map((code, i) => (
                <div key={i}>{code}</div>
              ))}
            </div>
            <button 
              onClick={downloadBackupCodes}
              className="edit-btn"
              style={{ fontSize: 13 }}
            >
              Download Backup Codes
            </button>
          </div>

          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Verify Setup</h3>
          <p style={{ marginBottom: 12, color: '#6b7280', fontSize: 14 }}>
            Enter the 6-digit code from your authenticator app to complete setup:
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, maxWidth: 200 }}>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="form-input"
                style={{ fontFamily: 'monospace', fontSize: 18, textAlign: 'center', letterSpacing: 4 }}
                maxLength={6}
              />
            </div>
            <button 
              onClick={handleVerifyAndEnable}
              disabled={verificationCode.length !== 6}
              className="save-btn"
            >
              Verify & Enable
            </button>
            <button 
              onClick={() => { setStep('status'); setSetupData(null); setVerificationCode(''); }}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Disable View */}
      {step === 'disable' && (
        <div style={{ padding: '20px 0' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Disable Two-Factor Authentication</h3>
          <p style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
            To disable 2FA, please enter your password and a verification code from your authenticator app.
          </p>
          
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Verification Code (optional but recommended)</label>
            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="form-input"
              style={{ fontFamily: 'monospace', fontSize: 18, textAlign: 'center', letterSpacing: 4, maxWidth: 200 }}
              maxLength={6}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button 
              onClick={handleDisable}
              disabled={!disablePassword}
              className="cancel-btn"
              style={{ background: '#ef4444', color: '#fff', border: 'none' }}
            >
              Disable 2FA
            </button>
            <button 
              onClick={() => { setStep('status'); setDisablePassword(''); setDisableCode(''); }}
              className="edit-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Change Password Form Component
// ChangePasswordForm removed - functionality integrated into main settings page
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChangePasswordForm_OLD: React.FC = () => {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    
    if (!password || password.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const API_URL = (import.meta.env.VITE_API_URL || 'https://cityscrapeai-production.up.railway.app').replace(/\/+$/, '');
      const token = localStorage.getItem('auth_token') || '';
      const resp = await fetch(`${API_URL}/api/custom-auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ current_password: current, new_password: password })
      });
      const text = await resp.text();
      let data: any = {};
      try { data = text ? JSON.parse(text) : {}; } catch {}
      
      if (!resp.ok) throw new Error(data?.detail || 'Failed to change password');
      
      setMessage('Password updated successfully.');
      setCurrent('');
      setPassword('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="password-form">
      {message && <div className="form-message success">{message}</div>}
      {error && <div className="form-message error">{error}</div>}
      
      <div className="form-group">
        <label className="form-label">Current password</label>
        <input 
          type="password" 
          value={current} 
          onChange={(e) => setCurrent(e.target.value)} 
          className="form-input"
        />
      </div>
      <div className="form-group">
        <label className="form-label">New password</label>
        <input 
          type="password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          className="form-input"
        />
      </div>
      <div className="form-group">
        <label className="form-label">Confirm new password</label>
        <input 
          type="password" 
          value={confirm} 
          onChange={(e) => setConfirm(e.target.value)} 
          className="form-input"
        />
      </div>
      <button 
        type="submit" 
        disabled={isSubmitting} 
        className="change-password-btn"
      >
        {isSubmitting ? 'Updating…' : 'Change password'}
      </button>
    </form>
  );
};

export default Settings;